import { compileFigure } from './compiler.js';
import { btreeFixture, btreeGuidedLookupTargets } from './fixtures/btree.js';
import { stackFramesFixture } from './fixtures/stack-frames.js';
import { sliceFixture } from './fixtures.js';
import { compileMotion } from './motion.js';
import type { FigureSpec, MotionPlan, ResolvedFigure } from './model.js';
import {
  anchoredTo,
  assertSemanticGate,
  ordered,
  pointsTo,
  routesThrough,
  spans,
  touchable,
  unchangedAcross,
  type SemanticAssertion,
  type SemanticGateResult,
} from './semantic-gate.js';
import { noOverlap } from './semantic-gate.js';
import { queueSelectTargets, queueViewMomentFixture } from './fixtures/queue-view.js';

export type TransitionVerb = 'allocate' | 'insert' | 'move' | 'replace' | 'split' | 'connect' | 'reveal' | 'invalidate' | 'return';
export const transitionVerbs = ['allocate', 'insert', 'move', 'replace', 'split', 'connect', 'reveal', 'invalidate', 'return'] as const satisfies readonly TransitionVerb[];

export interface FigureProgramState {
  id: string;
  label: string;
  description: string;
  spec: FigureSpec;
}

export interface SemanticAction {
  id: string;
  verb: TransitionVerb;
  subjects: string[];
  object?: string;
  description: string;
}

export interface FigureProgramTransition {
  id: string;
  from: string;
  to: string;
  actions: SemanticAction[];
}

export interface StableCorrespondence {
  id: string;
  fromState: string;
  fromSemanticId: string;
  toState: string;
  toSemanticId: string;
  description: string;
}

export interface FigureProgramCheckpoint {
  id: string;
  state: string;
  label: string;
  explanation: string;
  prompt?: string;
  expectedSemanticId?: string;
}

export interface FigureProgramInvariant {
  id: string;
  description: string;
  assertions: SemanticAssertion[];
}

export interface FigureProgram {
  id: string;
  version: 1;
  family: FigureSpec['family'];
  title: string;
  outcome: string;
  states: FigureProgramState[];
  transitions: FigureProgramTransition[];
  correspondences: StableCorrespondence[];
  checkpoints: FigureProgramCheckpoint[];
  invariants: FigureProgramInvariant[];
}

export interface ResolvedProgramState {
  id: string;
  label: string;
  description: string;
  figure: ResolvedFigure;
}

export interface ResolvedProgramTransition extends FigureProgramTransition {
  motion: MotionPlan;
  correspondenceIds: string[];
}

export interface ResolvedProgramInvariant {
  id: string;
  description: string;
  result: SemanticGateResult;
}

export interface ResolvedFigureProgram {
  id: string;
  version: 1;
  family: FigureSpec['family'];
  title: string;
  outcome: string;
  states: ResolvedProgramState[];
  transitions: ResolvedProgramTransition[];
  correspondences: StableCorrespondence[];
  checkpoints: FigureProgramCheckpoint[];
  invariants: ResolvedProgramInvariant[];
  fingerprint: string;
}

/** Resolve all named states once, derive deterministic motion, then enforce semantic truth. */
export function compileProgram(program: FigureProgram): ResolvedFigureProgram {
  validateProgramShape(program);
  const states = program.states.map((state) => ({
    id: state.id,
    label: state.label,
    description: state.description,
    figure: compileFigure(state.spec),
  }));
  const figures = Object.fromEntries(states.map((state) => [state.id, state.figure]));

  for (const correspondence of program.correspondences) {
    requireSemantic(figures[correspondence.fromState]!, correspondence.fromSemanticId, `correspondence “${correspondence.id}” source`);
    requireSemantic(figures[correspondence.toState]!, correspondence.toSemanticId, `correspondence “${correspondence.id}” target`);
  }
  for (const checkpoint of program.checkpoints) {
    if (checkpoint.expectedSemanticId) requireSemantic(figures[checkpoint.state]!, checkpoint.expectedSemanticId, `checkpoint “${checkpoint.id}” target`);
  }

  const transitions = program.transitions.map((transition): ResolvedProgramTransition => {
    const from = figures[transition.from]!;
    const to = figures[transition.to]!;
    for (const action of transition.actions) {
      for (const subject of action.subjects) requireSemanticEither(from, to, subject, `action “${action.id}” subject`);
      if (action.object) requireSemanticEither(from, to, action.object, `action “${action.id}” object`);
    }
    const motion = compileMotion(from, to);
    if (!motion) throw new Error(`figure program “${program.id}”: transition “${transition.id}” cannot compile motion; family and viewport must remain stable`);
    const correspondenceIds = program.correspondences
      .filter((item) => item.fromState === transition.from && item.toState === transition.to)
      .map((item) => item.id);
    return { ...transition, motion, correspondenceIds };
  });

  const invariants = program.invariants.map((invariant): ResolvedProgramInvariant => ({
    id: invariant.id,
    description: invariant.description,
    result: assertSemanticGate(figures, invariant.assertions, `figure program “${program.id}” invariant “${invariant.id}”`),
  }));

  const resolvedWithoutFingerprint = {
    id: program.id,
    version: program.version,
    family: program.family,
    title: program.title,
    outcome: program.outcome,
    states,
    transitions,
    correspondences: program.correspondences,
    checkpoints: program.checkpoints,
    invariants,
  };
  return { ...resolvedWithoutFingerprint, fingerprint: hash(stableStringify(resolvedWithoutFingerprint)) };
}

export function serializeResolvedProgram(program: ResolvedFigureProgram): string {
  return stableStringify(program);
}

export function sliceAppendProgram(width = 640): FigureProgram {
  const ready = sliceFixture(width, false, 'extent');
  const stagedValues = [...ready.values];
  stagedValues[ready.len] = 88;
  const written = { ...ready, id: 'program-slice-written', values: stagedValues, emphasis: [`array.cell.${ready.len}`] };
  const committed = { ...written, id: 'program-slice-committed', len: ready.len + 1 };
  const stable = ['slice.ptr', 'slice.cap'].flatMap((semanticId) => [
    correspondence('written', 'committed', semanticId),
  ]);
  return {
    id: 'program.slice-append-in-place.v1', version: 1, family: 'slice-header',
    title: 'Append into owned capacity',
    outcome: 'The value is inserted before len reveals it; pointer and capacity remain geometrically stable.',
    states: [state('ready', 'Owned capacity', ready), state('written', 'Value staged', written), state('committed', 'Length committed', committed)],
    transitions: [
      transition('write-next-cell', 'ready', 'written', action('insert-value', 'insert', [`array.cell.${ready.len}`], 'Insert 88 into the next owned cell.')),
      transition('commit-length', 'written', 'committed', action('reveal-value', 'reveal', [`array.cell.${ready.len}`], 'Reveal the inserted cell by advancing len.', 'slice.len')),
    ],
    correspondences: stable,
    checkpoints: [
      checkpoint('choose-cell', 'ready', 'Choose the destination', `array.cell.${ready.len}`),
      checkpoint('commit-len', 'written', 'Commit the visible extent', 'slice.len'),
      checkpoint('done', 'committed', 'Append complete'),
    ],
    invariants: [
      invariant('pointer-truth', 'The pointer terminates at the addressed backing-array cell in every state.', [
        pointsTo('ready', 'slice.ptr', `array.cell.${ready.targetIndex}`), pointsTo('written', 'slice.ptr', `array.cell.${ready.targetIndex}`), pointsTo('committed', 'slice.ptr', `array.cell.${ready.targetIndex}`),
      ]),
      invariant('visible-extent', 'The committed len bracket spans exactly the five visible cells.', [
        spans('committed', 'slice.len.extent', range('array.cell.', committed.len)),
      ]),
      invariant('stable-header', 'Committing len cannot move the pointer or capacity words.', [
        unchangedAcross('written', 'committed', ['slice.ptr', 'slice.cap']), anchoredTo('committed', 'slice.ptr.bottom', 'slice.ptr'),
      ]),
      invariant('guided-targets', 'Every learner target meets the phone touch floor.', [touchable('ready', [`array.cell.${ready.len}`]), touchable('written', ['slice.len'])]),
    ],
  };
}

export function sliceReallocationProgram(width = 640): FigureProgram {
  const base = { ...sliceFixture(width), id: 'program-slice-full', len: 4, cap: 4, targetIndex: 0, values: [100, 42, 7, 3], pointer: '0xc0…a0', pointerAddress: '0xc0000140a0' };
  const allocated = { ...base, id: 'program-slice-allocated', cap: 8, pointer: '0xc0…00', pointerAddress: '0xc000020000', values: [null, null, null, null, null, null, null, null] };
  const moved = { ...allocated, id: 'program-slice-moved', values: [100, 42, 7, 3, null, null, null, null] };
  const committed = { ...moved, id: 'program-slice-reallocated', len: 5, values: [100, 42, 7, 3, 88, null, null, null] };
  return {
    id: 'program.slice-reallocate.v1', version: 1, family: 'slice-header', title: 'Append after capacity is exhausted',
    outcome: 'Allocate, replace the pointer, move existing values, insert the new value, then reveal it with len.',
    states: [state('full', 'Capacity exhausted', base), state('allocated', 'New array allocated', allocated), state('moved', 'Values moved', moved), state('committed', 'Append committed', committed)],
    transitions: [
      transition('grow', 'full', 'allocated', action('allocate-array', 'allocate', range('array.cell.', 8), 'Allocate eight destination cells.'), action('replace-pointer', 'replace', ['slice.ptr'], 'Replace the backing-array pointer.', 'array.cell.0')),
      transition('relocate', 'allocated', 'moved', action('move-values', 'move', range('array.cell.', 4), 'Move the four existing values to the new allocation.')),
      transition('append', 'moved', 'committed', action('insert-new-value', 'insert', ['array.cell.4'], 'Insert 88.'), action('reveal-new-value', 'reveal', ['array.cell.4'], 'Advance len to reveal 88.', 'slice.len')),
    ],
    correspondences: [correspondence('allocated', 'moved', 'slice.ptr'), correspondence('moved', 'committed', 'slice.ptr')],
    checkpoints: [checkpoint('capacity', 'full', 'Prove the slice is full', 'slice.cap'), checkpoint('destination', 'allocated', 'Choose the copy destination', 'array.cell.0'), checkpoint('append-cell', 'moved', 'Choose the append cell', 'array.cell.4'), checkpoint('done', 'committed', 'Growth complete')],
    invariants: [
      invariant('pointer-truth', 'The old and replacement pointers each terminate at their own cell zero.', [pointsTo('full', 'slice.ptr', 'array.cell.0'), pointsTo('allocated', 'slice.ptr', 'array.cell.0')]),
      invariant('allocated-extent', 'The capacity bracket spans exactly the new eight-cell allocation.', [spans('allocated', 'slice.cap.extent', range('array.cell.', 8))]),
      invariant('touchable-decisions', 'Capacity and both destination cells remain touchable.', [touchable('full', ['slice.cap']), touchable('allocated', ['array.cell.0']), touchable('moved', ['array.cell.4'])]),
    ],
  };
}

export function stackCallProgram(width = 640): FigureProgram {
  const entry = stackFramesFixture(width, false, 'entry', 'serve-http');
  const call = stackFramesFixture(width, false, 'call', 'load-account');
  const returned = stackFramesFixture(width, false, 'return', 'serve-http');
  return {
    id: 'program.stack-call-return.v1', version: 1, family: 'stack-frames', title: 'Push a callee, then return control',
    outcome: 'The callee receives distinct storage, returns, and the caller resumes without losing identity or position.',
    states: [state('caller', 'Caller active', entry), state('callee', 'Callee pushed', call), state('returned', 'Caller resumed', returned)],
    transitions: [
      transition('call', 'caller', 'callee', action('connect-callee', 'connect', ['frame.serve-http'], 'Connect the call site to a new callee frame.', 'frame.load-account'), action('reveal-callee', 'reveal', ['frame.load-account'], 'Reveal the pushed frame.')),
      transition('return', 'callee', 'returned', action('return-value', 'return', ['frame.load-account'], 'Return control and preserve the callee as historical evidence.', 'frame.serve-http')),
    ],
    correspondences: [correspondence('caller', 'callee', 'frame.serve-http'), correspondence('callee', 'returned', 'frame.serve-http'), correspondence('callee', 'returned', 'frame.load-account')],
    checkpoints: [checkpoint('caller', 'caller', 'Find the caller', 'frame.serve-http'), checkpoint('callee', 'callee', 'Find the new frame', 'frame.load-account'), checkpoint('resumed', 'returned', 'Caller resumed', 'frame.serve-http')],
    invariants: [
      invariant('caller-continuity', 'The caller occupies the same resolved geometry while the callee runs and returns.', [unchangedAcross('callee', 'returned', ['frame.serve-http'])]),
      invariant('addressable-frames', 'Both caller and callee remain first-class touch targets.', [touchable('callee', ['frame.serve-http', 'frame.load-account']), anchoredTo('callee', 'frame.load-account.center', 'frame.load-account')]),
    ],
  };
}

export function btreeLookupProgram(width = 640): FigureProgram {
  const root = btreeFixture(width, false, 'root');
  const branch = btreeFixture(width, false, 'branch-right');
  const leaf = btreeFixture(width, false, 'leaf-d');
  const record = { ...leaf, id: 'program-btree-record', emphasis: [btreeGuidedLookupTargets[5]] };
  return {
    id: 'program.btree-lookup-62.v1', version: 1, family: 'btree', title: 'Route key 62 to one evidenced record',
    outcome: 'Ordered separators discard two subtrees before key 62 resolves to row:062.',
    states: [state('root', 'Compare root', root), state('branch', 'Choose ≥40', branch), state('leaf', 'Verify key', leaf), state('record', 'Resolve record', record)],
    transitions: [
      transition('root-to-branch', 'root', 'branch', action('connect-root-range', 'connect', [btreeGuidedLookupTargets[1]], 'Follow the ≥40 partition.', btreeGuidedLookupTargets[2])),
      transition('branch-to-leaf', 'branch', 'leaf', action('connect-leaf-range', 'connect', [btreeGuidedLookupTargets[3]], 'Follow the ≥60 partition.', 'btree.node.leaf-d'), action('reveal-key', 'reveal', [btreeGuidedLookupTargets[4]], 'Reveal exact key 62.')),
      transition('key-to-record', 'leaf', 'record', action('reveal-record', 'reveal', [btreeGuidedLookupTargets[5]], 'Reveal row:062 as the lookup evidence.', btreeGuidedLookupTargets[4])),
    ],
    correspondences: [correspondence('root', 'branch', 'btree.node.root'), correspondence('branch', 'leaf', 'btree.node.branch-right'), correspondence('leaf', 'record', 'btree.node.leaf-d')],
    checkpoints: [checkpoint('root', 'root', 'Start at the root', btreeGuidedLookupTargets[0]), checkpoint('range', 'branch', 'Choose the ≥40 range', btreeGuidedLookupTargets[1]), checkpoint('key', 'leaf', 'Verify key 62', btreeGuidedLookupTargets[4]), checkpoint('record', 'record', 'Resolve row:062', btreeGuidedLookupTargets[5])],
    invariants: [
      invariant('truthful-route', 'The resolved partitions form the exact root → branch-right → leaf-d route.', [routesThrough('leaf', ['btree.node.root', 'btree.node.branch-right', 'btree.node.leaf-d'], 'partitions')]),
      invariant('ordered-leaf', 'Leaf keys retain strict left-to-right order.', [ordered('leaf', ['btree.key.leaf-d.60', 'btree.key.leaf-d.62', 'btree.key.leaf-d.65'], 'x')]),
      invariant('stable-tree', 'Focusing the result cannot re-layout the tree.', [unchangedAcross('leaf', 'record', ['btree.node.root', 'btree.node.branch-right', 'btree.node.leaf-d']), anchoredTo('leaf', 'btree.key.leaf-d.62.center', 'btree.key.leaf-d.62')]),
      invariant('guided-targets', 'Every lookup decision is directly touchable.', [touchable('root', [btreeGuidedLookupTargets[0]]), touchable('branch', [btreeGuidedLookupTargets[1]]), touchable('leaf', [btreeGuidedLookupTargets[4]]), touchable('record', [btreeGuidedLookupTargets[5]])]),
    ],
  };
}

function validateProgramShape(program: FigureProgram): void {
  if (program.version !== 1) throw new Error(`figure program “${program.id}”: unsupported version ${program.version}`);
  if (!program.id.trim() || !program.title.trim() || !program.outcome.trim()) throw new Error('figure program: id, title, and outcome are required');
  if (program.states.length < 2) throw new Error(`figure program “${program.id}”: at least two named states are required`);
  unique(program.states.map((state) => state.id), `figure program “${program.id}” state`);
  unique(program.transitions.map((item) => item.id), `figure program “${program.id}” transition`);
  unique(program.correspondences.map((item) => item.id), `figure program “${program.id}” correspondence`);
  unique(program.checkpoints.map((item) => item.id), `figure program “${program.id}” checkpoint`);
  unique(program.invariants.map((item) => item.id), `figure program “${program.id}” invariant`);
  const states = new Map(program.states.map((state) => [state.id, state]));
  const first = program.states[0]!.spec;
  for (const state of program.states) {
    if (state.spec.family !== program.family) throw new Error(`figure program “${program.id}”: state “${state.id}” is ${state.spec.family}, expected ${program.family}`);
    if (state.spec.viewport.width !== first.viewport.width || state.spec.viewport.height !== first.viewport.height) throw new Error(`figure program “${program.id}”: state “${state.id}” changes viewport; state-pair motion requires one viewport`);
  }
  for (const transition of program.transitions) {
    if (!states.has(transition.from) || !states.has(transition.to)) throw new Error(`figure program “${program.id}”: transition “${transition.id}” references unknown state ${!states.has(transition.from) ? transition.from : transition.to}`);
    if (!transition.actions.length) throw new Error(`figure program “${program.id}”: transition “${transition.id}” requires at least one semantic action`);
    unique(transition.actions.map((action) => action.id), `transition “${transition.id}” action`);
    transition.actions.forEach((item) => { if (!item.subjects.length) throw new Error(`figure program “${program.id}”: action “${item.id}” requires a subject`); });
  }
  for (const item of program.correspondences) if (!states.has(item.fromState) || !states.has(item.toState)) throw new Error(`figure program “${program.id}”: correspondence “${item.id}” references an unknown state`);
  for (const item of program.checkpoints) if (!states.has(item.state)) throw new Error(`figure program “${program.id}”: checkpoint “${item.id}” references unknown state “${item.state}”`);
  for (const item of program.invariants) if (!item.assertions.length) throw new Error(`figure program “${program.id}”: invariant “${item.id}” requires at least one assertion`);
}

function state(id: string, label: string, spec: FigureSpec): FigureProgramState { return { id, label, description: label, spec }; }
function transition(id: string, from: string, to: string, ...actions: SemanticAction[]): FigureProgramTransition { return { id, from, to, actions }; }
function action(id: string, verb: TransitionVerb, subjects: string[], description: string, object?: string): SemanticAction { return { id, verb, subjects, ...(object ? { object } : {}), description }; }
function correspondence(fromState: string, toState: string, semanticId: string): StableCorrespondence { return { id: `${fromState}:${semanticId}:${toState}`, fromState, fromSemanticId: semanticId, toState, toSemanticId: semanticId, description: `${semanticId} preserves identity` }; }
function checkpoint(id: string, stateId: string, label: string, expectedSemanticId?: string): FigureProgramCheckpoint { return { id, state: stateId, label, explanation: label, ...(expectedSemanticId ? { prompt: label, expectedSemanticId } : {}) }; }
function invariant(id: string, description: string, assertions: SemanticAssertion[]): FigureProgramInvariant { return { id, description, assertions }; }
function range(prefix: string, count: number): string[] { return Array.from({ length: count }, (_, index) => `${prefix}${index}`); }

function requireSemantic(figure: ResolvedFigure, semanticId: string, context: string): void {
  const found = figure.shapes.some((item) => item.semanticId === semanticId)
    || figure.anchors.some((item) => item.semanticId === semanticId)
    || figure.hitRegions.some((item) => item.semanticId === semanticId)
    || figure.relations.some((item) => item.from === semanticId || item.to === semanticId);
  if (!found) throw new Error(`figure program: ${context} “${semanticId}” does not exist in resolved state “${figure.id}”`);
}

function requireSemanticEither(from: ResolvedFigure, to: ResolvedFigure, semanticId: string, context: string): void {
  try { requireSemantic(from, semanticId, context); } catch { requireSemantic(to, semanticId, context); }
}

function unique(values: string[], label: string): void {
  const seen = new Set<string>();
  values.forEach((value) => { if (!value.trim()) throw new Error(`${label} id must not be blank`); if (seen.has(value)) throw new Error(`${label} id “${value}” is duplicated`); seen.add(value); });
}

function stableStringify(value: unknown): string {
  return JSON.stringify(canonical(value));
}

function canonical(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(canonical);
  if (value && typeof value === 'object') return Object.fromEntries(Object.entries(value).sort(([left], [right]) => left.localeCompare(right)).map(([key, child]) => [key, canonical(child)]));
  return value;
}

function hash(value: string): string {
  let result = 0x811c9dc5;
  for (let index = 0; index < value.length; index += 1) {
    result ^= value.charCodeAt(index);
    result = Math.imul(result, 0x01000193);
  }
  return `program-${(result >>> 0).toString(16).padStart(8, '0')}`;
}

/**
 * A standing, saturated checkout queue sampled at three moments 200 ms apart.
 *
 * Nothing is served and nothing is admitted inside that window, so the same
 * eight requests are present in all three states and each one only gets older.
 * The client deadline is geometrically identical in every state, which is what
 * makes the taught moment honest: a bar that crosses the line crossed it by
 * waiting, not because the figure redrew its scale.
 */
export function queueOverloadProgram(width = 640): FigureProgram {
  const standing = queueViewMomentFixture(width, 'standing');
  const crossing = queueViewMomentFixture(width, 'crossing');
  const compounding = queueViewMomentFixture(width, 'compounding');
  const waiting = standing.items.map((item) => `queue.item.${item.id}`);
  const overdueAt = (semanticId: string): string => `${semanticId}.overdue`;
  const held = ['queue.deadline', 'queue.capacity', 'queue.flow'];
  const stable = [...waiting, 'queue.deadline', 'queue.capacity'].flatMap((semanticId) => [
    correspondence('standing', 'crossing', semanticId),
    correspondence('crossing', 'compounding', semanticId),
  ]);

  return {
    id: 'program.queue-ages-its-work.v1', version: 1, family: 'queue-view',
    title: 'A standing queue ages the work it holds',
    outcome: 'Two hundred milliseconds at a time, the same eight checkouts get older against a deadline that never moves, so one more answer becomes worthless at every step.',
    states: [
      state('standing', 'Queue stands full', standing),
      state('crossing', 'One more crosses', crossing),
      state('compounding', 'Three are already dead', compounding),
    ],
    transitions: [
      transition('age-200ms', 'standing', 'crossing',
        action('advance-waits', 'move', waiting, 'Two hundred milliseconds pass. Every wait in the queue grows by 200 ms; the deadline does not move.'),
        action('cross-deadline', 'invalidate', [queueSelectTargets.crossing], 'GET /orders/9014 passes the 1900 ms client timeout. The client has stopped waiting, so the rest of its wait is wasted work.', queueSelectTargets.deadline)),
      transition('age-again', 'crossing', 'compounding',
        action('advance-waits-again', 'move', waiting, 'Another 200 ms passes. The pool has still not finished a checkout.'),
        action('cross-deadline-again', 'invalidate', [queueSelectTargets.next], 'POST /payments crosses the same timeout. Three of eight answers are now unwanted before they are computed.', queueSelectTargets.deadline)),
    ],
    correspondences: stable,
    checkpoints: [
      checkpoint('head', 'standing', 'Find the wait that is already too late', queueSelectTargets.head),
      checkpoint('deadline', 'standing', 'Find the line the client stops waiting at', queueSelectTargets.deadline),
      checkpoint('crossed', 'crossing', 'Find the wait that just crossed it', overdueAt(queueSelectTargets.crossing)),
      checkpoint('wall', 'crossing', 'Find the wall that decides who is admitted', queueSelectTargets.capacity),
      checkpoint('doomed', 'compounding', 'Find the answers nobody is waiting for', queueSelectTargets.doomed),
    ],
    invariants: [
      invariant('bounded-depth', 'Depth never exceeds capacity: every queued item is drawn above the capacity wall in every state.', [
        ordered('standing', [...waiting, queueSelectTargets.capacity], 'y'),
        ordered('crossing', [...waiting, queueSelectTargets.capacity], 'y'),
        ordered('compounding', [...waiting, queueSelectTargets.capacity], 'y'),
      ]),
      invariant('head-is-oldest', 'The overdue part of each wait shortens from head to tail, so the head is always the most overdue request.', [
        ordered('compounding', [queueSelectTargets.head, queueSelectTargets.crossing, queueSelectTargets.next].map(overdueAt), 'x', 'descending'),
      ]),
      invariant('deadline-never-moves', 'The deadline, the capacity wall, the service rail and the age axis are geometrically identical in all three states, so growth is measured, not rescaled.', [
        unchangedAcross('standing', 'crossing', [...held, 'queue.axis']),
        unchangedAcross('crossing', 'compounding', [...held, 'queue.axis']),
        anchoredTo('compounding', 'queue.deadline.axis', 'queue.deadline'),
      ]),
      invariant('addressable-rows', 'Every queued item owns its own hit region and no two rows overlap. Row height caps the target at 28 px at 640 and 42 px at 390, below the 44 px phone floor.', [
        noOverlap('standing', waiting),
        touchable('standing', waiting, 28),
        touchable('crossing', [queueSelectTargets.capacity, queueSelectTargets.deadline], 21),
      ]),
    ],
  };
}
